import * as ToggleGroup from "@radix-ui/react-toggle-group";
import type { FC } from "react";
import { GBFlagIcon, USFlagIcon } from "@/components/ui/icons/FlagIcons";
import type { SiteVariant } from "@/context/VariantContext";
import { useVariant } from "@/context/VariantContext";
import type { BaseComponentProps } from "@/types/component-props";
import { cn } from "@/utils/cn";
import { themeComponentStyles } from "@/utils/themeUtils";
import { setVariantAndReload } from "@/utils/variant";

export interface VariantToggleGroupProps extends BaseComponentProps {
  /** Whether to show labels next to flags (default: false) */
  showLabels?: boolean;
}

/**
 * VariantToggleGroup
 *
 * A toggle group for switching between UK and US site variants.
 * Uses country flags for visual representation.
 * When switched, sets a cookie and reloads the page for the change to take effect.
 */
export const VariantToggleGroup: FC<VariantToggleGroupProps> = ({
  className,
  showLabels = false,
}) => {
  const { variant } = useVariant();

  const handleValueChange = (value: string) => {
    // Only trigger if a new value is selected (not deselecting current)
    if (value && value !== variant) {
      setVariantAndReload(value as SiteVariant);
    }
  };

  return (
    <ToggleGroup.Root
      aria-label="Select site variant"
      className={cn(
        "inline-flex items-center justify-center p-0.5",
        themeComponentStyles.getContainerClasses({
          isSchemeToggleGroup: true,
        }),
        className,
      )}
      onValueChange={handleValueChange}
      type="single"
      value={variant}
    >
      <ToggleGroup.Item
        aria-label="United States"
        className={cn(
          "inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium transition-standard cursor-pointer mx-0.5",
          themeComponentStyles.getControlClasses(),
          "radix-state-on:bg-theme-primary/10 dark:radix-state-on:bg-gray-900 font-medium",
          "radix-state-off:opacity-50 radix-state-off:hover:opacity-100",
          "hover:bg-theme-muted/70",
        )}
        value="us"
      >
        <USFlagIcon className={cn("h-4 w-4", showLabels && "mr-1")} />
        {showLabels && <span>US</span>}
      </ToggleGroup.Item>
      <ToggleGroup.Item
        aria-label="United Kingdom"
        className={cn(
          "inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium transition-standard cursor-pointer mx-0.5",
          themeComponentStyles.getControlClasses(),
          "radix-state-on:bg-theme-primary/10 dark:radix-state-on:bg-gray-900 font-medium",
          "radix-state-off:opacity-50 radix-state-off:hover:opacity-100",
          "hover:bg-theme-muted/70",
        )}
        value="uk"
      >
        <GBFlagIcon className={cn("h-4 w-4", showLabels && "mr-1")} />
        {showLabels && <span>UK</span>}
      </ToggleGroup.Item>
    </ToggleGroup.Root>
  );
};
